(ns common.transaction-process.permissions)

(def permission-hierarchy
  "Permission levels from lowest to highest."
  [:none :r :w])

(def permission-rank
  (zipmap permission-hierarchy (range)))

(defn higher-permission
  [a b]
  (let [ra (get permission-rank a 0)
        rb (get permission-rank b 0)]
    (if (>= ra rb) a b)))

(defn resolve-account-field-permission
  "Resolve the effective permission for a single account on a single field at a
   given stage. Takes into account all team-role memberships the account has.

   Arguments:
     :template          - the template config map
     :team-assignments  - map of {team-role-id [{:value account-id :label name} ...]}
     :account-id        - the account db/id to resolve for
     :field-kw          - the field keyword (e.g. :transaction-process/short-name)
     :stage-id          - the current stage keyword (e.g. :preparation)
     :read-only-user?   - true if the account is a read-only user in classic zapflow teams
     :admin?            - true if the account is a tenant admin; bypasses the team
                          matrix and grants :w (still subject to :read-only-user?
                          downgrade). Pass false/nil at display sites that should
                          show the raw team-matrix permission instead.

   Returns :w, :r, or :none."
  [{:keys [template team-assignments account-id field-kw stage-id read-only-user? admin?]}]
  (let [effective (if admin?
                    :w
                    (let [permissions-matrix (get-in template [:template/permissions field-kw stage-id])
                          team-roles (:template/team-roles template)
                          account-roles (->> team-roles
                                             (keep (fn [role]
                                                     (let [role-id (:team-role/id role)
                                                           members (get team-assignments role-id [])]
                                                       (when (some (fn [m]
                                                                     (= (or (:value m) m) account-id))
                                                                   members)
                                                         role-id)))))]
                      (reduce
                       (fn [acc role-id]
                         (let [perm (get permissions-matrix role-id :none)]
                           (higher-permission acc perm)))
                       :none
                       account-roles)))]
    (if (and read-only-user? (= effective :w))
      :r
      effective)))

(defn resolve-account-permissions
  "Resolve all field permissions for an account across all stages.

   Returns {field-kw {stage-id :r/:w/:none}}."
  [{:keys [template team-assignments account-id read-only-user? admin?]}]
  (let [permissions (:template/permissions template)
        stages (->> (:template/stages template)
                    (map :stage/id))]
    (->> permissions
         (map (fn [[field-kw _stage-perms]]
                [field-kw
                 (->> stages
                      (map (fn [stage-id]
                             [stage-id
                              (resolve-account-field-permission
                               {:template         template
                                :team-assignments  team-assignments
                                :account-id        account-id
                                :field-kw          field-kw
                                :stage-id          stage-id
                                :read-only-user?   read-only-user?
                                :admin?            admin?})]))
                      (into {}))]))
         (into {}))))

(defn resolve-field-permission
  "Effective field permission for an account WITH the runtime policy that a TP with
   no team assignments yet is fully writable (:w) — so a freshly-created TP can be
   filled in before any team is assigned. Otherwise defers to the team matrix
   (resolve-account-field-permission). This is the single source for both the
   visible/editable field derivation and the sub-entity can-write checks; the
   permissions *modal* shows the raw matrix and so calls
   resolve-account-field-permission directly.

   `:locked?` caps the result at :r (a completed TP is read-only — there's no way
   to save edits while completed; reopening clears the lock). It downgrades :w to
   :r so fields/attachments stay visible but uneditable, and makes
   `can-write-field?` false everywhere."
  [{:keys [team-assignments locked?] :as ctx}]
  (let [perm (if (some seq (vals team-assignments))
               (resolve-account-field-permission ctx)
               :w)]
    (if (and locked? (= :w perm)) :r perm)))

(defn can-write-field?
  "Check if an account has write permission for a specific field at a given stage.
   Useful for sub-entity permission checks — e.g., pass :attestation/outcome to check
   whether the user can create/edit attestations. Uses resolve-field-permission, so
   it agrees with the field visible/editable derivation (incl. the no-team-members
   ⇒ writable policy)."
  [ctx]
  (= :w (resolve-field-permission ctx)))

(defn decision-ready?
  "True when a decision is in its ready (approved) state rather than a draft. An
   absent `:decision/draft?` counts as ready, matching how the modals coerce the
   flag with `boolean`."
  [decision]
  (not (boolean (:decision/draft? decision))))

(defn approval-gated-write?
  "True when writing `incoming` over `stored` crosses a decision slot's approval
   gate, so the slot's `:slot/approval-permission-field` must be checked on top
   of the ordinary decision write permission.

   Both save actions write `:decision/draft?`, so saving a ready decision as a
   draft un-approves it just as surely as saving a draft as ready approves it.
   Either direction counts, and so does any edit of an already-ready decision —
   otherwise a user without the approval permission could rewrite an approved
   decision's conditions or reservations underneath it.

   `stored` is nil while the decision is being created; a decision can be created
   straight into the ready state, which is why the create path needs this too.
   A write with no `:decision/draft?` at all reads as ready, so the gate fails
   closed."
  [{:keys [stored incoming]}]
  (or (decision-ready? incoming)
      (and (some? stored) (decision-ready? stored))))

(defn resolve-role-permissions
  "Resolve permissions for a team role across all fields and stages.

   Returns {field-kw {stage-id :r/:w/:none}}."
  [{:keys [template role-id]}]
  (let [permissions (:template/permissions template)
        stages (->> (:template/stages template)
                    (map :stage/id))]
    (->> permissions
         (map (fn [[field-kw _stage-perms]]
                [field-kw
                 (->> stages
                      (map (fn [stage-id]
                             [stage-id
                              (get-in permissions [field-kw stage-id role-id] :none)]))
                      (into {}))]))
         (into {}))))
